import React, { forwardRef, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Empty, Input, Table, Tag } from '@maxtropy/components';
import titleIcon from '@/assets/images/title_icon.png';
import { formatHours, formatPercent, getRange } from '../../utils';
import type { AnalysisGroup, AnalysisRow, LoadRangeKey } from '../../types';
import styles from './index.module.scss';

interface DetailSectionProps {
  mediumType: number;
  mediumName: string;
  groups: AnalysisGroup[];
  keyword: string;
  onKeywordChange: (value: string) => void;
}

const getRankClass = (index: number) => {
  if (index === 0) return styles.rankFirst;
  if (index === 1) return styles.rankSecond;
  if (index === 2) return styles.rankThird;
  return styles.rankNormal;
};

const renderRank = (index: number) => (
  <span className={getRankClass(index)}>
    <span>{index + 1}</span>
  </span>
);

const DIMENSION_TAGS: Partial<Record<AnalysisRow['dimension'], string>> = {
  plant: '35kV总站主变',
  station: '二级配电站',
  area: '三级变电站',
  line: '出线开关',
  outlet: '供气出口',
  compressor: '压缩机',
};

const GAS_DIMENSION_TAGS: Partial<Record<AnalysisRow['dimension'], string>> = {
  plant: '全厂气源',
  station: '燃气站',
  outlet: '出口总管',
  area: '区域分管',
};

const LOAD_RANGE_COLORS: Record<LoadRangeKey, string> = {
  low: '#16DD8E',
  mid: '#FFCB47',
  high: '#FF9247',
};

const getDimensionTag = (row: AnalysisRow, mediumType: number) =>
  mediumType === 0 ? DIMENSION_TAGS[row.dimension] : GAS_DIMENSION_TAGS[row.dimension] ?? DIMENSION_TAGS[row.dimension];

const renderName = (row: AnalysisRow, mediumType: number) => {
  const tag = getDimensionTag(row, mediumType);
  return (
    <div className={styles.objectCell}>
      <span className={styles.objectName}>{row.name}</span>
      {tag ? <Tag>{tag}</Tag> : null}
    </div>
  );
};

const getLoadRateTagColor = (value?: number) => {
  if (value === undefined || value === null) return 'default';
  const percent = Math.abs(value) <= 1 ? value * 100 : value;
  if (percent < 60) return 'success';
  if (percent < 80) return 'warning';
  return 'error';
};

const renderMaxLoadRate = (value?: number) => (
  <Tag border="solid" color={getLoadRateTagColor(value)}>
    {formatPercent(value)}
  </Tag>
);

const renderDuration = (row: AnalysisRow, key: LoadRangeKey, color: string) => {
  const range = getRange(row, key);
  return <span style={{ color }}>{formatHours(range?.duration)}</span>;
};

const renderShare = (row: AnalysisRow, key: LoadRangeKey, color: string) => {
  const range = getRange(row, key);
  const value = Number(range?.durationPercent ?? 0);
  const width = value <= 1 ? value * 100 : value;
  return (
    <div className={styles.share}>
      <span className={styles.shareTrack}>
        <span className={styles.shareBar} style={{ width: `${Math.min(width, 100)}%`, background: color }} />
      </span>
      <span className={styles.shareValue}>{formatPercent(range?.durationPercent)}</span>
    </div>
  );
};

const DetailSection = forwardRef<HTMLDivElement, DetailSectionProps>(
  ({ mediumType, mediumName, groups, keyword, onKeywordChange }, ref) => {
    const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(() => new Set());
    const isCompactScreen = window.innerWidth <= 2560;
    const tableScrollWidth = isCompactScreen ? 1340 : 1950;
    const detailDescription =
      mediumType === 0
        ? '一级电力站房、配电站、变电站、出线按层级平铺，共用同一套筛选条件'
        : '全厂、站房、出口、区域按层级平铺，共用同一套筛选条件';
    const columnWidths = useMemo(
      () =>
        isCompactScreen
          ? {
              rank: 60,
              object: 220,
              maxLoadRate: 112,
              maxLoadTime: 150,
              lowDuration: 112,
              lowRatio: 135,
              midDuration: 126,
              midRatio: 135,
              highDuration: 150,
              highRatio: 140,
            }
          : {
              rank: 80,
              object: 360,
              maxLoadRate: 150,
              maxLoadTime: 220,
              lowDuration: 155,
              lowRatio: 210,
              midDuration: 170,
              midRatio: 210,
              highDuration: 185,
              highRatio: 210,
            },
      [isCompactScreen]
    );

    const handleToggleGroup = (key: string) => {
      setCollapsedKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    };

    const columns = useMemo<ColumnsType<AnalysisRow>>(
      () => [
        {
          title: '排名',
          width: columnWidths.rank,
          align: 'center',
          render: (_value, _row, index) => renderRank(index),
        },
        {
          title: '统计对象',
          dataIndex: 'name',
          width: columnWidths.object,
          render: (_value, row) => renderName(row, mediumType),
        },
        {
          title: '最高负载率',
          dataIndex: 'maxLoadRate',
          width: columnWidths.maxLoadRate,
          render: value => renderMaxLoadRate(value),
        },
        {
          title: '发生时间',
          dataIndex: 'maxLoadTime',
          width: columnWidths.maxLoadTime,
          render: value => value ?? '--',
        },
        {
          title: '低负荷0~60%',
          width: columnWidths.lowDuration,
          render: (_value, row) => renderDuration(row, 'low', LOAD_RANGE_COLORS.low),
        },
        {
          title: '占本对象',
          width: columnWidths.lowRatio,
          render: (_value, row) => renderShare(row, 'low', LOAD_RANGE_COLORS.low),
        },
        {
          title: '常用负荷60~80%',
          width: columnWidths.midDuration,
          render: (_value, row) => renderDuration(row, 'mid', LOAD_RANGE_COLORS.mid),
        },
        {
          title: '占本对象',
          width: columnWidths.midRatio,
          render: (_value, row) => renderShare(row, 'mid', LOAD_RANGE_COLORS.mid),
        },
        {
          title: '较高负荷80%以上',
          width: columnWidths.highDuration,
          render: (_value, row) => renderDuration(row, 'high', LOAD_RANGE_COLORS.high),
        },
        {
          title: '占本对象',
          width: columnWidths.highRatio,
          render: (_value, row) => renderShare(row, 'high', LOAD_RANGE_COLORS.high),
        },
      ],
      [columnWidths, mediumType]
    );

    return (
      <div className={styles.detail} ref={ref}>
        <div className={styles.detailHeader}>
          <div>
            <img className={styles.detailTitleIcon} src={titleIcon} alt="" />
            <span className={styles.detailTitle}>{mediumName}各层级负载率明细</span>
            <span className={styles.detailDescription}>{detailDescription}</span>
          </div>
          <div className={styles.search}>
            <span>名称搜索：</span>
            <Input.Search
              value={keyword}
              allowClear
              style={{ width: 220 }}
              placeholder="请输入统计对象名称"
              onChange={event => onKeywordChange(event.target.value)}
            />
          </div>
        </div>
        <div className={styles.tables}>
          {groups.length ? (
            groups.map(group => {
              const collapsed = collapsedKeys.has(group.key);
              return (
                <div className={styles.tableGroup} key={group.key} data-dimension={group.key}>
                  <div className={styles.groupTitle}>
                    <img src={group.icon} alt="" />
                    <span className={styles.groupName}>{group.name} 负载率明细</span>
                    <span className={styles.groupDescription}>{group.description}负载率分层独立排名</span>
                    <div className={styles.groupAction}>
                      <span className={styles.groupCount}>
                        <span className={styles.groupCountValue}>{group.count}</span>
                        <span className={styles.groupCountUnit}> 个对象</span>
                      </span>
                      <div className={styles.groupToggle} onClick={() => handleToggleGroup(group.key)}>
                        {collapsed ? '展开' : '收起'}
                      </div>
                    </div>
                  </div>
                  {!collapsed && (
                    <Table<AnalysisRow>
                      rowKey="key"
                      rowClassName={() => styles.dataRow}
                      columns={columns}
                      dataSource={group.rows}
                      pagination={false}
                      scroll={{ x: tableScrollWidth }}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <Empty className={styles.empty} />
          )}
        </div>
      </div>
    );
  }
);

DetailSection.displayName = 'DetailSection';

export default DetailSection;
